import { getCollision } from "./CollisionMap";
import type { CollisionMap } from "./CollisionMap";
import type { SceneTreeItemSize } from "@editor/items/scene/SceneTreeItem";

const GRID_SIZE = 10;

interface Point {
  x: number;
  y: number;
}

export interface LineData<PointType extends Point = Point> {
  id: string;
  start: PointType;
  end: PointType;
  direction: "left" | "right" | "up" | "down";
  type: "line" | "curve";
  dimension: "x" | "y";
  sign: 1 | -1;
  top: number;
  bottom: number;
  left: number;
  right: number;
}

export class Line<PointType extends Point = Point> implements LineData {
  id: string;
  start: PointType;
  end: PointType;
  direction: "left" | "right" | "up" | "down";
  type: "line" | "curve";
  dimension: "x" | "y";
  sign: 1 | -1;
  top: number;
  bottom: number;
  left: number;
  right: number;

  constructor(data: Partial<LineData> & { start: PointType; end: PointType }) {
    const { start, end } = data;
    this.id = crypto.randomUUID();
    this.bottom = 0;
    this.top = 0;
    this.left = 0;
    this.right = 0;

    this.start = {
      ...start,
      x: start.x,
      y: start.y
    };
    this.end = {
      ...end,
      x: end.x,
      y: end.y
    };
    this.dimension = "x";
    this.direction = "left";
    this.sign = 1;

    this.type = "line";

    this.updateBounds();
    this.updateDirection();
  }

  static getLineDirection(pointA: Point, pointB: Point) {
    if (pointA.x < pointB.x) return "right";
    else if (pointA.x > pointB.x) return "left";
    else if (pointA.y < pointB.y) return "down";
    else if (pointA.y > pointB.y) return "up";
    return undefined;
  }

  /**
   * Measure a line.
   * Lines are only straight vertical or horizontal.
   */
  static getLength(line: Line) {
    if (line.dimension === "y") {
      return Math.abs(line.bottom - line.top);
    } else {
      return Math.abs(line.right - line.left);
    }
  }

  static clone<ClonePointType extends Point>(line: Line<ClonePointType>) {
    return new Line<ClonePointType>({
      start: {
        ...line.start
      },
      end: {
        ...line.end
      }
    });
  }

  static areCrossing(lineAData: LineData, lineBData: LineData) {
    if (lineAData.id == lineBData.id) return false;
    const lineA = new Line({ start: lineAData.start, end: lineAData.end });
    const lineB = new Line({ start: lineBData.start, end: lineBData.end });
    return lineA.crossesOtherLine(lineB);
  }

  static areOverlapping(lineAData: LineData, lineBData: LineData) {
    if (lineAData.id == lineBData.id) return false;
    if (lineAData == lineBData) return false;
    if (lineAData.dimension != lineBData.dimension) return false;

    if (lineAData.dimension == "x") {
      if (lineAData.top != lineBData.top) return false;
      return (
        lineAData.right > lineBData.left && lineAData.left < lineBData.right
      );
    } else {
      if (lineAData.left != lineBData.left) return false;
      return (
        lineAData.bottom > lineBData.top && lineAData.top < lineBData.bottom
      );
    }
  }

  static log(
    ...args: (string | number | Line | Line[] | { [k: number]: Line })[]
  ) {
    const lines: {
      [key: number | string]: ReturnType<typeof Line.makeLoggable>;
    } = {};
    for (let i = 0; i < args.length; i++) {
      const arg = args[i];
      if (typeof arg === "string") {
        console.log(arg);
      } else if (Array.isArray(arg)) {
        arg.forEach(
          (l, index) => (lines[`${i}.${index}`] = Line.makeLoggable(l))
        );
      } else if (typeof arg === "object" && "direction" in arg) {
        lines[i] = Line.makeLoggable(arg);
      } else if (typeof arg === "object") {
        for (const key in arg) {
          lines[key] = Line.makeLoggable(arg[key]);
        }
      }
    }
    console.table(lines, ["direction", "startX", "startY", "endX", "endY"]);
  }

  static makeLoggable(line: Line) {
    return {
      ...line,
      startX: line.start.x,
      startY: line.start.y,
      endX: line.end.x,
      endY: line.end.y
    };
  }

  get length(): number {
    return Line.getLength(this);
  }

  get center() {
    return {
      x: (this.start.x + this.end.x) / 2,
      y: (this.start.y + this.end.y) / 2
    };
  }

  get normal() {
    return {
      x: Math.sign(this.end.x - this.start.x),
      y: Math.sign(this.end.y - this.start.y)
    };
  }

  get staticDimension() {
    return this.dimension == "x" ? "y" : "x";
  }

  get x() {
    return this.left;
  }
  get y() {
    return this.top;
  }

  /**
   * Returns a point on the line.
   * @param percent between 1 and 0.
   */
  get(percent: number) {
    return {
      x: this.start.x + (this.end.x - this.start.x) * percent,
      y: this.start.y + (this.end.y - this.start.y) * percent
    };
  }

  /**
   *
   */
  moveX(amount: number) {
    this.start.x = this.start.x + amount;
    this.end.x = this.end.x + amount;
    this.updateBounds();
  }
  /**
   *
   */
  moveY(amount: number) {
    this.start.y = this.start.y + amount;
    this.end.y = this.end.y + amount;
    this.updateBounds();
  }

  /**
   *
   * @param amount How much should it be shortened by?
   * @param fromStart Cut the amount from the start of the line?
   */
  shorten(amount: number, fromStart = false) {
    if (fromStart) {
      if (this.direction === "left") {
        this.start.x -= amount;
      } else if (this.direction === "right") {
        this.start.x += amount;
      } else if (this.direction === "up") {
        this.start.y -= amount;
      } else {
        this.start.y += amount;
      }
    } else {
      if (this.direction === "left") {
        this.end.x += amount;
      } else if (this.direction === "right") {
        this.end.x -= amount;
      } else if (this.direction === "up") {
        this.end.y += amount;
      } else {
        this.end.y -= amount;
      }
    }
    this.updateDirection();
    this.updateBounds();
    return true;
  }

  /**
   *
   * @param amount How much should it be lengthened by?
   * @param fromStart Cut the amount from the start of the line?
   */
  lengthen(amount: number, fromStart = false) {
    return this.shorten(-amount, fromStart);
  }

  updateDirection() {
    this.direction =
      Line.getLineDirection(this.start, this.end) || this.direction || "left";
    if (this.direction === "left" || this.direction === "right") {
      this.dimension = "x";
    } else {
      this.dimension = "y";
    }
    if (this.direction === "left" || this.direction === "up") this.sign = -1;
    else this.sign = 1;
  }

  updateBounds() {
    const startX = this.start.x;
    const endX = this.end.x;
    if (startX < endX) {
      this.left = startX;
      this.right = endX;
    } else {
      this.left = endX;
      this.right = startX;
    }

    const startY = this.start.y;
    const endY = this.end.y;
    if (startY < endY) {
      this.top = startY;
      this.bottom = endY;
    } else {
      this.top = endY;
      this.bottom = startY;
    }
  }

  get toSVGString() {
    return `L${this.end.x} ${this.end.y}`;
  }

  /**
   * check if this line collides with an element
   */
  collidesWithItem(item: SceneTreeItemSize, margin = 0) {
    if (this.dimension === "x") {
      return (
        item.right + margin > this.left &&
        item.left - margin < this.right &&
        item.top - 12 < this.bottom &&
        item.bottom + 12 > this.top
      );
    } else {
      return (
        item.right + 12 > this.left &&
        item.left - 12 < this.right &&
        item.top - margin < this.bottom &&
        item.bottom + margin > this.top
      );
    }
  }

  /**
   * Get the first colliding element from a list of elements.
   */
  getCollidingItem(
    collisionMap: CollisionMap,
    margin = 0,
    ignoredNetItemUids: string[] = []
  ) {
    if (this.dimension === "x") {
      return getCollision(
        collisionMap,
        this.left - margin,
        this.top - GRID_SIZE,
        this.right + margin,
        this.bottom + GRID_SIZE,
        ignoredNetItemUids
      );
    }
    if (this.dimension === "y") {
      return getCollision(
        collisionMap,
        this.left - GRID_SIZE,
        this.top - margin,
        this.right + GRID_SIZE,
        this.bottom + margin,
        ignoredNetItemUids
      );
    }
    return undefined;
  }

  /**
   * Check if a straight line is overlapping another.
   * @returns The first line that overlaps.
   */
  getOverlappingLine<T extends Line = Line>(lines: T[]) {
    return lines.find((line) => this.overlaps(line));
  }

  /**
   * Check if a straight line is overlapping another.
   */
  overlaps(line: Line) {
    if (!this.couldOverlap(line)) return false;

    if (this.dimension == "x") return this.top == line.top;
    else return this.left == line.left;
  }

  /**
   * If the line was moved sideways (not in its own dimension),
   * could it theoretically overlap this line?
   * @param line The line to compare
   */
  couldOverlap(line: Line) {
    if (this.id == line.id) return false;
    if (this.dimension != line.dimension) return false;
    if (this.dimension == "y") {
      return this.top < line.bottom && line.top < this.bottom;
    } else {
      return this.left < line.right && line.left < this.right;
    }
  }

  clone() {
    return new Line({
      start: {
        x: this.start.x,
        y: this.start.y
      },
      end: {
        x: this.end.x,
        y: this.end.y
      }
    });
  }

  /**
   *
   * @param {Line} otherLine
   */
  crossesOtherLine(otherLine: Line) {
    if (otherLine.id == this.id) return false;
    if (!otherLine) return false;
    return (
      this.left <= otherLine.right &&
      this.right >= otherLine.left &&
      this.bottom >= otherLine.top &&
      this.top <= otherLine.bottom
    );
  }

  toObject() {
    return {
      id: this.id,
      top: this.top,
      left: this.left,
      bottom: this.bottom,
      right: this.right,
      start: this.start,
      end: this.end,
      direction: this.direction,
      type: this.type,
      dimension: this.dimension,
      length: this.length
    };
  }
  toJSON() {
    return {
      x:
        this.dimension == "y"
          ? this.start.x
          : `${this.start.x} → ${this.end.x}`,
      y:
        this.dimension == "x" ? this.start.y : `${this.start.y} → ${this.end.y}`
    };
  }
}

export default Line;
